'use client';

import { useEffect, useState } from 'react';

import { cn } from '../../utils/cn';

import type { TocItem } from '../helpers/build-toc';

/**
 * ArticleToc — the sticky "On this page" table of contents shared by the docs
 * and blog article pages. Scroll-spies the rendered `## ` headings (matched by
 * id) and smooth-scrolls on click. Side-agnostic: the caller positions it (docs
 * place it on the right, the blog on the left).
 */
export const ArticleToc = ({ items }: { items: TocItem[] }): React.JSX.Element => {
	const [activeId, setActiveId] = useState<string>(items[0]?.id ?? '');

	useEffect(() => {
		if (items.length === 0) return;

		const headings = items
			.map((item) => document.getElementById(item.id))
			.filter((el): el is HTMLElement => el !== null);

		if (headings.length === 0) return;

		const observer = new IntersectionObserver(
			(entries) => {
				// Prefer the topmost heading currently intersecting the upper band.
				const visible = entries
					.filter((entry) => entry.isIntersecting)
					.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
				if (visible[0]) {
					setActiveId(visible[0].target.id);
				}
			},
			{ rootMargin: '0px 0px -70% 0px', threshold: 0 }
		);

		headings.forEach((heading) => observer.observe(heading));
		return () => observer.disconnect();
	}, [items]);

	const handleClick = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
		const target = document.getElementById(id);
		if (!target) return;
		event.preventDefault();
		target.scrollIntoView({ behavior: 'smooth', block: 'start' });
		setActiveId(id);
		history.replaceState(null, '', `#${id}`);
	};

	if (items.length === 0) return <></>;

	return (
		<nav>
			<div className="mb-[14px] text-[11px] font-semibold uppercase tracking-wide text-neutral/40">
				On this page
			</div>
			<div className="flex flex-col border-l border-neutral/10">
				{items.map((item) => {
					const isActive = item.id === activeId;
					return (
						<a
							key={item.id}
							href={`#${item.id}`}
							onClick={(event) => handleClick(event, item.id)}
							aria-current={isActive ? 'location' : undefined}
							className={cn(
								'-ml-px py-[5.5px] pl-[14px] text-[13px] transition-colors',
								isActive
									? 'border-l-2 border-accent font-semibold text-accent'
									: 'border-l-2 border-transparent font-medium text-neutral/60 hover:text-neutral'
							)}
						>
							{item.text}
						</a>
					);
				})}
			</div>
		</nav>
	);
};
